import { Button } from "@material-ui/core";
import { httpClient } from "api/httpClient";
import { ReferenceInput, SelectInput } from "react-admin";
import { StoragePlaceReferenceInput } from "components/inputs/StoragePlaceReferenceInput";
import { EditPopup } from "components/fields/EditableField";
import { envSettings } from "core/EnvSettings";
import React from "react";
import ReactDOM from "react-dom";
import { EditForm } from "components/fields/EditableField";
import { SaveContextProvider, useNotify, useTranslate } from "react-admin";
import { useNotifyError } from "utils/notifiers/useNotifyError";

export const StoragePlaceEditComponent = ({ terminalId, ...props }) => {
	return (
		<StoragePlaceReferenceInput
			terminalId={terminalId}
			source="storage_place_id"
			filter={{ is_blocked: false, include_virtual: false }}
			{...props}
		/>
	);
};

export const AreaEditComponent = ({ terminalId, ...props }) => {
	return (
		<ReferenceInput
			source="area_id"
			reference="areas"
			sort={{ field: "sort_order", order: "ASC" }}
			filter={{ terminal_id: [terminalId], activity_state: "active", no_range: true }}
			{...props}
		>
			<SelectInput optionText="name" />
		</ReferenceInput>
	);
};

export const PalletAreaStoreButton = ({ pallet, order, onSuccess, allowStorePallet }) => {
	const translate = useTranslate();
	const isAllowedStorePallet = allowStorePallet(pallet);

	if (envSettings.REACT_APP_FF_STORAGE_MODULE) return null;

	return (
		<SaveContextProvider>
			<EditPopup
				record={pallet}
				resource="pallets"
				editable={isAllowedStorePallet}
				onSuccess={onSuccess}
				render={({ openPopup }) => (
					<Button
						size="small"
						variant="outlined"
						color="primary"
						disabled={!isAllowedStorePallet}
						onClick={(e) => {
							e.stopPropagation();
							openPopup && openPopup(e);
						}}
						style={{ marginLeft: 8 }}
					>
						{translate("tab.pallets.store")}
					</Button>
				)}
				edit={<AreaEditComponent terminalId={order?.terminal_id} />}
			/>
		</SaveContextProvider>
	);
};


const EditComponent = ({ terminalId, ...props }) => {
	if (envSettings.REACT_APP_FF_STORAGE_MODULE) {
		return (
			<StoragePlaceEditComponent
				terminalId={terminalId}
				{...props}
			/>
		);
	}

	return <AreaEditComponent terminalId={terminalId} {...props} />;
};

export const PalletStoreButton = ({ pallet, order, onSuccess }) => {
	const translate = useTranslate();

	return (
		<SaveContextProvider>
			<EditPopup
				record={pallet}
				resource="pallets"
				editable={true}
				onSuccess={onSuccess}
				render={({ openPopup }) => (
					<Button
						size="small"
						variant="outlined"
						color="primary"
						onClick={(e) => {
							e.stopPropagation();
							openPopup && openPopup(e);
						}}
						style={{ marginLeft: 8 }}
					>
						{translate("tab.pallets.store")}
					</Button>
				)}
				edit={
					<EditComponent
						terminalId={order?.terminal_id}
						source="storage_place_id"
					/>
				}
			/>
		</SaveContextProvider>
	);
};

export const PalletAllocateButton = ({ pallet, order, onSuccess }) => {
	const translate = useTranslate();
	const notify = useNotify();
	const notifyError = useNotifyError();
	const [anchorEl, setAnchorEl] = React.useState(null);
	const [loading, setLoading] = React.useState(false);

	if (!envSettings.REACT_APP_FF_STORAGE_MODULE) return null;

	const handleOpen = (e) => {
		e.stopPropagation();
		setAnchorEl(e.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleSubmit = async (values) => {
		if (!order?.id || !pallet?.id) return;

		const storagePlaceId = values.storage_place_id;

		if (!storagePlaceId) {
			notify("Please select a storage place", { type: "warning" });
			return;
		}

		setLoading(true);

		try {
			// Call allocation API with manual allocation
			// Pass pallet_ids and storage_place_ids to run allocation algorithm with filtered places
			const { json } = await httpClient(`order/${order.id}/pallets/allocate-storage-places`, {
				method: "POST",
				body: JSON.stringify({
					apply_allocation: true,
					pallet_ids: [pallet.id],
					storage_place_ids: [storagePlaceId],
					allow_reallocation: true,
				}),
			});

			// Close the popup
			setAnchorEl(null);

			// Show success notification
			notify("ra.notification.updated", { type: "info", messageArgs: { smart_count: 1 } });

			// Call onSuccess with the allocation result (will show AllocationResultDialog)
			if (onSuccess) {
				onSuccess(json);
			}
		} catch (err) {
			notifyError(err);
		} finally {
			setLoading(false);
		}
	};

	const open = Boolean(anchorEl);

	return (
		<SaveContextProvider>
			{/* Reuse EditForm component - same UI as PalletStoreButton */}
			{open &&
				ReactDOM.createPortal(
					<EditForm
						anchorEl={anchorEl}
						handleSubmit={handleSubmit}
						handleClose={handleClose}
						loading={loading}
						edit={<StoragePlaceEditComponent terminalId={order?.terminal_id} />}
						record={pallet}
						resource="pallets"
					/>,
					document.body
				)}
			<Button
				size="small"
				variant="outlined"
				color="secondary"
				onClick={handleOpen}
				style={{ marginLeft: 8 }}
			>
				{translate("tab.pallets.allocate")}
			</Button>
		</SaveContextProvider>
	);
};
